import {
  Content,
  Group,
  Item,
  Label,
  Portal,
  Root,
  Separator,
  Sub,
  SubContent,
  SubTrigger,
  Trigger,
} from "@radix-ui/react-dropdown-menu";
import classNames from "classnames/bind";
import React, { PropsWithChildren, ReactNode } from "react";
import { Icon, IconLoading } from "../Icon";
import { Skeleton } from "../Skeleton";
import styles from "./DropdownMenu.module.scss";
import {
  DropdownMenuItemProps,
  DropdownMenuOption,
  DropdownMenuProps,
} from "./types";

const cx = classNames.bind(styles);

/**
 * Menu representing a set of actions, triggered by the consumer-provided element
 */
export const DropdownMenu = ({
  options,
  rootProps,
  contentProps,
  children,
}: PropsWithChildren<DropdownMenuProps>) => {
  const renderOptions = (opts: DropdownMenuOption[]): ReactNode => {
    return opts.map((opt, idx) =>
      renderOption(opt, {
        idx,
        isLast: idx === opts.length - 1,
        isNextOptAGroupOrSeparator: Boolean(
          idx + 1 < opts.length &&
            ((opts[idx + 1].isGroup && opts[idx + 1].options) ||
              opts[idx + 1].isSeparator)
        ),
      })
    );
  };

  const renderOption = (
    opt: DropdownMenuOption,
    {
      idx,
      isLast,
      isNextOptAGroupOrSeparator,
    }: { idx: number; isLast: boolean; isNextOptAGroupOrSeparator: boolean }
  ): ReactNode => {
    if (opt.isGroup && opt.options && opt.label) {
      return (
        <Group
          key={`${opt.label}-${idx}`}
          className={cx("group")}
        >
          <Label
            className={cx("group-label")}
            aria-label={opt.label}
          >
            {opt.label}
          </Label>
          {renderOptions(opt.options)}
          {!isLast && !isNextOptAGroupOrSeparator ? (
            <Separator className={cx("separator")} />
          ) : null}
        </Group>
      );
    }

    if (!opt.isSeparator && !opt.isGroup && opt.options) {
      return (
        <Sub key={`${opt.label}-${idx}`}>
          <Skeleton
            isLoading={opt.isLoading}
            isBusy={opt.isBusy}
          >
            <SubTrigger
              className={cx("item")}
              disabled={opt.isDisabled}
            >
              {opt.slotLeft ? (
                <div className={cx("slot", "left")}>
                  {opt.slotLeft({ iconProps: { size: 16 } })}
                </div>
              ) : null}
              {opt.label}
              <div className={cx("slot", "right")}>
                {opt.isBusy ? (
                  <IconLoading
                    speed="fast"
                    size={16}
                  />
                ) : (
                  <Icon
                    variant="caret-right"
                    size={16}
                  />
                )}
              </div>
            </SubTrigger>
          </Skeleton>
          <SubContent className={cx("sub-content")}>
            {renderOptions(opt.options)}
          </SubContent>
        </Sub>
      );
    }

    return (
      <DropdownMenuItem
        key={`${opt.label}-${idx}`}
        option={opt}
      />
    );
  };

  const {
    className: contentClassName,
    style: contentStyle,
    ...restContentProps
  } = contentProps ?? {};

  return (
    <Root {...rootProps}>
      <Trigger asChild>{children}</Trigger>
      <Portal>
        <Content
          className={cx(`content`, contentClassName)}
          sideOffset={2}
          collisionPadding={16}
          {...restContentProps}
          style={contentStyle}
        >
          {renderOptions(options)}
        </Content>
      </Portal>
    </Root>
  );
};

export const DropdownMenuItem = ({ option }: DropdownMenuItemProps) => {
  // recursive rendering or group rendering is not supported
  if (option.isGroup || option.options) {
    return null;
  }

  if (option.isSeparator) {
    return <Separator className={cx("separator")} />;
  }

  return (
    <Skeleton
      isLoading={option.isLoading}
      isBusy={option.isBusy}
    >
      <Item
        disabled={option.isDisabled}
        className={cx(
          "item",
          option.isActive && !option.isPresentational && "active",
          option.variant &&
            !option.isPresentational &&
            `color-${option.variant}`,
          option.isPresentational && option.variant === "highlight" && "bold",
          option.onClick && "clickable",
          option.isPresentational && "presentational"
        )}
        onClick={(e) => {
          e.stopPropagation();
          option.onClick?.(e);
        }}
        {...(option.isPresentational
          ? { onSelect: (e) => e.preventDefault() }
          : {})}
        data-testid={`${option.label}`}
      >
        {option.slotLeft ? (
          <div className={cx("slot", "left")}>
            {option.slotLeft({ iconProps: { size: 16 } })}
          </div>
        ) : null}
        {option.label}
        {option.slotRight || option.flair || option.isBusy ? (
          <div className={cx("slot", "right")}>
            {option.isBusy ? (
              <IconLoading
                speed="fast"
                size={16}
              />
            ) : option.flair ? (
              <span className={cx("flair")}>{option.flair}</span>
            ) : option.slotRight ? (
              option.slotRight({ iconProps: { size: 16 } })
            ) : null}
          </div>
        ) : null}
      </Item>
    </Skeleton>
  );
};
